import React, { useEffect, useState } from 'react';
import { apiFetch } from '../api/client';
import { FileUploader } from '../components/FileUploader';

interface Document {
    id: number;
    name: string;
    description: string;
    url: string;
    docType: string;
    createdAt: string;
    updatedAt: string;
}

const DOC_TYPES = [
    { value: 'MENU', label: 'Menu Completo' },
    { value: 'MENU_BAR', label: 'Menu Bar' },
    { value: 'MENU_RISTORANTE', label: 'Menu Ristorante' },
    { value: 'MENU_CAFFETTERIA', label: 'Menu Caffetteria' },
    { value: 'MENU_COLAZIONE', label: 'Menu Colazione' },
    { value: 'MENU_PRANZO', label: 'Menu Pranzo' },
    { value: 'MENU_GELATO', label: 'Menu Gelato' },
    { value: 'MENU_TAGLIERE', label: 'Menu Tagliere' },
    { value: 'MENU_FESTE', label: 'Menu Feste e Eventi' },
    { value: 'ALLERGENI', label: 'Allergeni' },
    { value: 'EVENTO', label: 'Eventi' }
];

export function DocumentManagement() {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedType, setSelectedType] = useState('MENU');
    const [uploadMode, setUploadMode] = useState(false);
    const [editingDoc, setEditingDoc] = useState<Document | null>(null);
    const [editForm, setEditForm] = useState({ name: '', description: '' });

    const tenantId = localStorage.getItem('tenantId') || '1';

    const loadDocuments = async () => {
        try {
            setLoading(true);

            // Usa cache se disponibile per migliorare la performance
            const cacheKey = `documents_${tenantId}`;
            const cached = sessionStorage.getItem(cacheKey);
            const cacheTime = sessionStorage.getItem(`${cacheKey}_time`);

            // Se la cache è recente (< 30 secondi), usala
            if (cached && cacheTime && (Date.now() - parseInt(cacheTime)) < 30000) {
                setDocuments(JSON.parse(cached));
                setLoading(false);
                return;
            }

            const res = await apiFetch(`/api/public/tenants/${tenantId}/documents`);
            if (res.ok) {
                const docs = await res.json();
                setDocuments(docs);

                // Salva in cache
                sessionStorage.setItem(cacheKey, JSON.stringify(docs));
                sessionStorage.setItem(`${cacheKey}_time`, Date.now().toString());
            }
        } catch (error) {
            console.error('Errore nel caricamento dei documenti:', error);
        } finally {
            setLoading(false);
        }
    };

    const clearCache = () => {
        const cacheKey = `documents_${tenantId}`;
        sessionStorage.removeItem(cacheKey);
        sessionStorage.removeItem(`${cacheKey}_time`);
    };

    useEffect(() => {
        loadDocuments();
    }, [tenantId]);

    const handleUpload = async (url: string, filename: string) => {
        try {
            const res = await apiFetch('/api/tenant/self/documents', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: selectedType,
                    url,
                    title: filename,
                    description: ''
                })
            });

            if (res.ok) {
                await loadDocuments(); // Ricarica la lista
                setUploadMode(false);
            } else {
                alert('Errore durante il salvataggio del documento');
            }
        } catch (error) {
            console.error('Errore upload:', error);
            alert('Errore durante l\'upload');
        }
    };

    const handleDelete = async (docId: number) => {
        if (!confirm('Sei sicuro di voler eliminare questo documento?')) return;

        try {
            const res = await apiFetch(`/api/tenant/self/documents/${docId}`, {
                method: 'DELETE'
            });

            if (res.ok) {
                await loadDocuments(); // Ricarica la lista
            } else {
                alert('Errore durante l\'eliminazione');
            }
        } catch (error) {
            console.error('Errore delete:', error);
            alert('Errore durante l\'eliminazione');
        }
    };

    const handleEdit = async (doc: Document) => {
        setEditingDoc(doc);
        setEditForm({ name: doc.name, description: doc.description });
    };

    const handleSaveEdit = async () => {
        if (!editingDoc) return;

        try {
            const res = await apiFetch(`/api/tenant/self/documents/${editingDoc.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title: editForm.name,
                    description: editForm.description
                })
            });

            if (res.ok) {
                await loadDocuments();
                setEditingDoc(null);
            } else {
                alert('Errore durante l\'aggiornamento');
            }
        } catch (error) {
            console.error('Errore update:', error);
            alert('Errore durante l\'aggiornamento');
        }
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTypeLabel = (type: string) => {
        return DOC_TYPES.find(dt => dt.value === type)?.label || type;
    };

    const documentsByType = documents.reduce((acc, doc) => {
        if (!acc[doc.docType]) acc[doc.docType] = [];
        acc[doc.docType].push(doc);
        return acc;
    }, {} as Record<string, Document[]>);

    if (loading) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.2rem', color: '#666' }}>Caricamento documenti...</div>
            </div>
        );
    }

    return (
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h1 style={{ color: 'var(--accent)', fontWeight: 700, fontSize: '2rem' }}>
                    Gestione Documenti
                </h1>
                <button
                    onClick={() => setUploadMode(!uploadMode)}
                    style={{
                        background: 'var(--accent)',
                        color: 'white',
                        border: 'none',
                        padding: '0.8rem 1.5rem',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        fontWeight: 600
                    }}
                >
                    {uploadMode ? 'Annulla' : '+ Carica Nuovo'}
                </button>
            </div>

            {uploadMode && (
                <div style={{
                    background: '#f7f8fc',
                    borderRadius: '1rem',
                    padding: '2rem',
                    marginBottom: '2rem',
                    border: '2px dashed var(--accent)'
                }}>
                    <h3 style={{ marginBottom: '1rem', color: 'var(--secondary)' }}>Carica Nuovo Documento</h3>
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                            Tipo di documento:
                        </label>
                        <select
                            value={selectedType}
                            onChange={e => setSelectedType(e.target.value)}
                            style={{
                                padding: '0.7rem 1rem',
                                borderRadius: '0.5rem',
                                border: '1px solid #ddd',
                                fontSize: '1rem',
                                width: '300px'
                            }}
                        >
                            {DOC_TYPES.map(type => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <FileUploader
                        label="Trascina qui il file o clicca per selezionare (PDF, JPG, PNG)"
                        onUpload={(url) => handleUpload(url, url.split('/').pop() || 'file')}
                    />
                </div>
            )}

            <div style={{ display: 'grid', gap: '2rem' }}>
                {Object.entries(documentsByType).map(([type, docs]) => (
                    <div key={type} style={{
                        background: 'white',
                        borderRadius: '1rem',
                        padding: '1.5rem',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                        <h2 style={{
                            color: 'var(--secondary)',
                            marginBottom: '1rem',
                            fontSize: '1.3rem',
                            borderBottom: '2px solid var(--accent)',
                            paddingBottom: '0.5rem'
                        }}>
                            {getTypeLabel(type)} ({docs.length})
                        </h2>

                        <div style={{ display: 'grid', gap: '1rem' }}>
                            {docs.map(doc => (
                                <div key={doc.id} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    padding: '1rem',
                                    background: '#f9f9f9',
                                    borderRadius: '0.5rem',
                                    border: '1px solid #eee'
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, color: 'var(--secondary)' }}>
                                            {doc.name}
                                        </div>
                                        {doc.description && (
                                            <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.2rem' }}>
                                                {doc.description}
                                            </div>
                                        )}
                                        <div style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.3rem' }}>
                                            Caricato: {formatDate(doc.createdAt)}
                                            {doc.updatedAt !== doc.createdAt && (
                                                <span> • Modificato: {formatDate(doc.updatedAt)}</span>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <a
                                            href={doc.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                background: '#43a047',
                                                color: 'white',
                                                padding: '0.5rem 1rem',
                                                borderRadius: '0.3rem',
                                                textDecoration: 'none',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            Visualizza
                                        </a>
                                        <button
                                            onClick={() => handleEdit(doc)}
                                            style={{
                                                background: '#2196f3',
                                                color: 'white',
                                                border: 'none',
                                                padding: '0.5rem 1rem',
                                                borderRadius: '0.3rem',
                                                cursor: 'pointer',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            Modifica
                                        </button>
                                        <button
                                            onClick={() => handleDelete(doc.id)}
                                            style={{
                                                background: '#f44336',
                                                color: 'white',
                                                border: 'none',
                                                padding: '0.5rem 1rem',
                                                borderRadius: '0.3rem',
                                                cursor: 'pointer',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            Elimina
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {editingDoc && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '1rem',
                        padding: '2rem',
                        width: '500px',
                        maxWidth: '90vw'
                    }}>
                        <h3 style={{ marginBottom: '1.5rem', color: 'var(--secondary)' }}>
                            Modifica Documento
                        </h3>
                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                                Nome:
                            </label>
                            <input
                                type="text"
                                value={editForm.name}
                                onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                style={{
                                    width: '100%',
                                    padding: '0.7rem',
                                    borderRadius: '0.5rem',
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                                Descrizione:
                            </label>
                            <textarea
                                value={editForm.description}
                                onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                                rows={3}
                                style={{
                                    width: '100%',
                                    padding: '0.7rem',
                                    borderRadius: '0.5rem',
                                    border: '1px solid #ddd',
                                    fontSize: '1rem',
                                    resize: 'vertical'
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setEditingDoc(null)}
                                style={{
                                    background: '#666',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.7rem 1.5rem',
                                    borderRadius: '0.5rem',
                                    cursor: 'pointer'
                                }}
                            >
                                Annulla
                            </button>
                            <button
                                onClick={handleSaveEdit}
                                style={{
                                    background: 'var(--accent)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.7rem 1.5rem',
                                    borderRadius: '0.5rem',
                                    cursor: 'pointer'
                                }}
                            >
                                Salva
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {documents.length === 0 && (
                <div style={{
                    textAlign: 'center',
                    padding: '3rem',
                    color: '#666',
                    fontSize: '1.1rem'
                }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
                    <div>Nessun documento caricato</div>
                    <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                        Clicca su "Carica Nuovo" per aggiungere il primo documento
                    </div>
                </div>
            )}
        </div>
    );
}